import * as React from "react";
import {Link, required, SaveButton, TextInput, useNotify, useRecordContext} from "react-admin";
import {useController, useFormContext} from "react-hook-form";

const ACCEPTED_IMAGE_TYPES = ['image/png', 'image/jpeg', 'image/jpg'];

const pad = (value) => `${value}`.padStart(2, '0');

const toInputDateTime = (value) => {
    if (!value) {
        return '';
    }

    if (typeof value === 'string') {
        return value.includes('T')
            ? value.slice(0, 16)
            : value.replace(' ', 'T').slice(0, 16);
    }

    const date = new Date(Number(value) * 1000);

    if (Number.isNaN(date.getTime())) {
        return '';
    }

    return `${date.getUTCFullYear()}-${pad(date.getUTCMonth() + 1)}-${pad(date.getUTCDate())}T${pad(date.getUTCHours())}:${pad(date.getUTCMinutes())}`;
};

const toApiDateTime = (value) => {
    if (!value) {
        return '';
    }

    return value.replace('T', ' ');
};

const CalendarEventImageInput = () => {
    const notify = useNotify();
    const record = useRecordContext();
    const {control} = useFormContext();
    const {field, fieldState} = useController({
        name: 'image',
        control,
        rules: {required: 'Required'},
        defaultValue: '',
    });
    const [preview, setPreview] = React.useState('');

    React.useEffect(() => {
        if (record?.image && !field.value) {
            field.onChange(record.image);
        }
    }, [field, record]);

    const uploadImage = (imageFile) => {
        if (!imageFile) {
            return;
        }

        if (!ACCEPTED_IMAGE_TYPES.includes(imageFile.type)) {
            notify(`Image should be in (.png, .jpeg, .jpg) formats.`, 'warning');
            return;
        }

        field.onChange(imageFile);

        const reader = new FileReader();
        reader.onload = () => {
            setPreview(`${reader.result}`);
        };
        reader.readAsDataURL(imageFile);
    };

    const imageSrc = preview || (typeof field.value === 'string' ? field.value : '');

    return (
        <div className={`form-field image-required${fieldState.error ? ' error' : ''}`}>
            <span className="fieldTitle">Image</span>
            <div>
                <div className="imageWrap">
                    {imageSrc ? (
                        <img className="image" src={imageSrc} alt="Calendar event" />
                    ) : (
                        <div
                            className="image"
                            style={{
                                background: '#F7F7F8',
                                border: '1px solid #E4E4E6',
                                borderRadius: '8px',
                            }}
                        />
                    )}
                    <p className="required-text">{fieldState.error?.message || 'Required'}</p>
                </div>
                <label className="imageLabel" htmlFor="calendarEventImageInput"></label>
                <input
                    type="file"
                    id="calendarEventImageInput"
                    className="fileInput"
                    accept="image/png, image/jpeg, image/jpg"
                    onBlur={field.onBlur}
                    onChange={(e) => uploadImage(e.currentTarget.files[0])}
                />
                <label htmlFor="calendarEventImageInput" className="uploadButton">
                    {imageSrc ? 'Update Image' : 'Choose an Image'}
                </label>
            </div>
        </div>
    );
};

export const CalendarEventToolbar = ({className = 'dailyActionToolbar'}) => (
    <div className={className}>
        <Link to="/calendar-events" className="button bar-purple-button">Cancel</Link>
        <SaveButton className="button bar-gradient-button" label="Save" alwaysEnable icon={<></>} />
    </div>
);

export const CalendarEventFormFields = ({className = 'tabbedFormWrap'}) => (
    <div className={className}>
        <TextInput className="formFields" label="Title" source="title" fullWidth validate={required()} multiline />
        <p className="required-text">Required</p>
        <TextInput className="formFields" label="Description" source="description" fullWidth multiline />
        <TextInput
            className="formFields"
            label="Date"
            source="date"
            fullWidth
            type="datetime-local"
            validate={required()}
            format={toInputDateTime}
            parse={toApiDateTime}
            InputLabelProps={{shrink: true}}
        />
        <p className="required-text">Required</p>
        <CalendarEventImageInput />
    </div>
);

export const CalendarEventImagePreview = ({record}) => {
    if (!record?.image) {
        return 'No image';
    }

    return (
        <img
            src={record.image}
            alt={record.title || 'Calendar event'}
            style={{
                width: '72px',
                height: '72px',
                objectFit: 'cover',
                borderRadius: '8px',
                border: '1px solid #E4E4E6',
            }}
        />
    );
};
